import type { KioskConfigDTO } from '@print-saas/shared';
import { useSecretGesture } from '../hooks/useSecretGesture';
import { BrandMark } from './BrandMark';

interface HeaderProps {
  config: KioskConfigDTO | null;
  apiUrl: string;
  /** Hidden technician gesture on the logo (long-press 5 s or triple-tap). */
  onSecret: () => void;
}

/** The business's own header: its logo and name, with its tagline below. The kiosk's name sits on the right. */
export function Header({ config, apiUrl, onSecret }: HeaderProps) {
  const gesture = useSecretGesture(onSecret);
  const branding = config?.branding ?? null;
  return (
    <header className="flex shrink-0 items-center justify-between gap-6 px-[4vmin] pt-[3vmin]">
      <div {...gesture} className="flex min-w-0 touch-none items-center gap-[2vmin]" aria-label={branding?.name ?? 'Print kiosk'}>
        {branding ? (
          <>
            <BrandMark branding={branding} apiUrl={apiUrl} className="size-[9vmin]" />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[3.6vmin] font-extrabold tracking-tight">{branding.name}</div>
              <div className="truncate text-[2.1vmin] font-medium text-muted">{branding.tagline}</div>
            </div>
          </>
        ) : (
          // Before the first config arrives: a neutral placeholder rather than someone else's brand.
          <div className="size-[9vmin] rounded-[1.8vmin] bg-panel ring-1 ring-line" />
        )}
      </div>
      {config && (
        <div className="min-w-0 shrink text-right leading-tight">
          <div className="truncate text-[2.6vmin] font-bold">{config.name}</div>
          {config.location && <div className="truncate text-[2vmin] text-muted">{config.location}</div>}
        </div>
      )}
    </header>
  );
}
